// Category rows and group headers on Overview: two lines each. Line 1 is the name (one line,
// ellipsis) and one primary number, which the Planned / Spent / Remaining toggle picks. Line 2 is
// a thin full-width bar (the pace tick sits on it) and a small "$spent of $planned". Status words
// only where they matter: over budget (red, with text), paid (a green check). View only: every
// number comes from the overview as it is; nothing here changes what a number means.
import type { GroupDto, OverviewDto, PaceStatus } from '../../../shared/api';
import { groupShare } from '../../../shared/groups';
import { esc, money, shortDate } from '../format';

type Category = OverviewDto['categories'][number];

// ---- The toggle ---------------------------------------------------------------------------

export type RowMode = 'remaining' | 'spent' | 'planned';
export const ROW_MODES: RowMode[] = ['planned', 'spent', 'remaining'];
export const ROW_MODE_LABEL: Record<RowMode, string> = { planned: 'Planned', spent: 'Spent', remaining: 'Remaining' };
export const DEFAULT_ROW_MODE: RowMode = 'remaining';
/** localStorage key: the choice is remembered per device. */
export const ROW_MODE_KEY = 'hush-row-mode';

export const parseRowMode = (v: unknown): RowMode => (ROW_MODES.includes(v as RowMode) ? (v as RowMode) : DEFAULT_ROW_MODE);

/** The stored choice; Remaining when nothing is stored or storage is not available. */
export function readRowMode(store: { getItem(key: string): string | null } | null | undefined): RowMode {
  try {
    return parseRowMode(store?.getItem(ROW_MODE_KEY) ?? null);
  } catch {
    return DEFAULT_ROW_MODE;
  }
}

export function writeRowMode(store: { setItem(key: string, value: string): void } | null | undefined, mode: RowMode): void {
  try {
    store?.setItem(ROW_MODE_KEY, mode);
  } catch {
    // A private window: the choice lasts until the page closes.
  }
}

/** The segmented control near the top of Overview. */
export function rowModeToggle(mode: RowMode): string {
  return `<div class="rowmode" role="group" aria-label="Show on each category">${ROW_MODES.map(
    (m) => `<button data-a="row-mode" data-mode="${m}" aria-pressed="${m === mode}">${ROW_MODE_LABEL[m]}</button>`,
  ).join('')}</div>`;
}

// ---- The bar --------------------------------------------------------------------------------

/** The pace tick's width in px (styles.css .tick). */
export const TICK_PX = 2;

/** CSS left for the tick at `pct` percent: the tick stays inside the bar at 0 and at 100. */
export function tickLeft(pct: number): string {
  const p = Math.min(100, Math.max(0, Number.isFinite(pct) ? pct : 0));
  return `calc(${+p.toFixed(2)}% - ${+((p * TICK_PX) / 100).toFixed(2)}px)`;
}

/** Above this many items an itemized bar is one continuous bar (the count is still on line 1). */
export const SEGMENT_MAX = 12;

export interface RowBar {
  /** Filled share, 0 to 100. */
  pct: number;
  tone: 'ok' | 'ahead' | 'over';
  /** Where spending should be today (0 to 100), or null for no tick. */
  tick: number | null;
  /** One segment per item, the first `filled` paid; null for one continuous bar. */
  segments: { count: number; filled: number } | null;
}

export type RowKind = 'normal' | 'itemized' | 'bill' | 'unset' | 'set-aside';

export interface RowView {
  kind: RowKind;
  id: string;
  name: string;
  /** Line 1, right. */
  primary: string;
  /** bad: over budget (red). ok: paid or done (green, with a check). muted: no amount set. */
  tone: '' | 'bad' | 'ok' | 'muted';
  /** Line 2, small and muted. */
  small: string;
  bar: RowBar | null;
  /** Read out for the bar ("Groceries, $0 of $475"). */
  barLabel: string;
  /** What a tap opens: the category screen, or (no amount set) the category editor. */
  action: 'category' | 'cat-drawer';
}

/** Itemized: the whole category against its plan; pace (extra only) can still flag ahead. */
export function rowStatus(d: OverviewDto, k: Category): PaceStatus {
  const p = d.pace[k.id];
  if (k.budgetCents <= 0) return 'unset';
  if (k.spentCents > k.budgetCents) return 'over';
  if (!k.itemized) return p?.status ?? 'ok';
  return p?.status === 'ahead' || p?.status === 'over' ? 'ahead' : 'ok';
}

/** A same-amount category's state: "Paid", or when it is due. */
export function billLabel(d: OverviewDto, k: Category): string {
  if (k.spentCents > k.budgetCents) return 'Over budget';
  if (k.spentCents > 0) return 'Paid';
  if (k.billDueDay === null) return 'Not paid yet';
  return `Due ${shortDate(`${d.month.key}-${String(Math.min(k.billDueDay, d.month.days)).padStart(2, '0')}`)}`;
}

const pctOf = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 0);

/** The primary number for a mode: "$475 left" / "$6 over", "$0 spent", "$475". */
function money3(mode: RowMode, spent: number, planned: number): { text: string; over: boolean } {
  const over = spent > planned;
  if (mode === 'spent') return { text: `${money(spent)} spent`, over };
  if (mode === 'planned') return { text: money(planned), over };
  return { text: over ? `${money(spent - planned)} over` : `${money(planned - spent)} left`, over };
}

/** Everything one row shows, for a mode. */
export function rowView(d: OverviewDto, k: Category, mode: RowMode): RowView {
  const base = { id: k.id, name: k.name, action: 'category' as const };
  const sp = k.spentCents;
  const plan = k.budgetCents;
  const small = `${money(sp)} of ${money(plan)}`;
  const barLabel = `${k.name}, ${small}`;

  // Money set aside: moved against planned. Never a pace or spending warning.
  if (k.kind === 'set_aside') {
    const done = plan > 0 && sp >= plan;
    const primary =
      mode === 'spent' ? `${money(sp)} moved` : mode === 'planned' ? money(plan) : plan <= 0 ? 'No amount planned' : done ? 'All done' : `${money(plan - sp)} to move`;
    const count = k.itemized && k.itemCount ? `, ${k.paidCount} of ${k.itemCount} done` : '';
    return {
      ...base,
      kind: 'set-aside',
      primary,
      tone: mode === 'remaining' && done ? 'ok' : plan <= 0 && mode === 'remaining' ? 'muted' : '',
      small: `${money(sp)} moved of ${money(plan)}${count}`,
      bar: { pct: pctOf(sp, plan), tone: 'ok', tick: null, segments: null },
      barLabel: `${k.name}, ${money(sp)} moved of ${money(plan)}`,
    };
  }

  // No amount set: muted, "Set an amount" (opens the category editor), no bar.
  if (plan <= 0) {
    return { ...base, kind: 'unset', primary: 'Set an amount', tone: 'muted', small: sp > 0 ? `${money(sp)} spent, not tracked` : 'Not tracked yet', bar: null, barLabel, action: 'cat-drawer' };
  }

  const st = rowStatus(d, k);
  const m = money3(mode, sp, plan);
  const overText = mode === 'remaining' && m.over;

  // Same amount every month: no bar; "Paid" with a check, or when it is due.
  if (k.sameAmount && !k.itemized) {
    const label = billLabel(d, k);
    const primary = mode === 'remaining' ? (m.over ? m.text : label) : m.text;
    return { ...base, kind: 'bill', primary, tone: overText ? 'bad' : sp > 0 && !m.over ? 'ok' : '', small, bar: null, barLabel };
  }

  // A list: one segment per item, filled as each is paid ("4 of 8 paid").
  if (k.itemized && k.itemCount > 0) {
    const allPaid = k.paidCount >= k.itemCount;
    const count = `${k.paidCount} of ${k.itemCount} paid`;
    const primary = mode === 'remaining' ? (m.over ? m.text : count) : m.text;
    const segmented = k.itemCount <= SEGMENT_MAX;
    return {
      ...base,
      kind: 'itemized',
      primary,
      tone: overText ? 'bad' : mode === 'remaining' && allPaid && !m.over ? 'ok' : '',
      small,
      bar: {
        pct: m.over ? 100 : pctOf(k.paidCount, k.itemCount),
        tone: m.over ? 'over' : st === 'ahead' ? 'ahead' : 'ok',
        tick: null,
        segments: segmented && !m.over ? { count: k.itemCount, filled: Math.min(k.paidCount, k.itemCount) } : null,
      },
      barLabel: `${barLabel}, ${count}`,
    };
  }

  // One amount (or a list with only room for other spending): spent against planned, the pace tick.
  const p = d.pace[k.id];
  return {
    ...base,
    kind: 'normal',
    primary: m.text,
    tone: overText ? 'bad' : '',
    small,
    bar: { pct: pctOf(sp, plan), tone: m.over ? 'over' : st === 'ahead' ? 'ahead' : 'ok', tick: k.itemized ? null : Math.round(pctOf(p?.expectedShare ?? 0, 1) * 10) / 10, segments: null },
    barLabel,
  };
}

const CHECK =
  '<svg class="okmark" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7.5l2.6 2.6L11 4.6"/></svg>';

function barHtml(v: RowView): string {
  const b = v.bar;
  if (!b) return '<span class="bar none" aria-hidden="true"></span>';
  const now = Math.round(b.pct);
  const aria = `role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${now}" aria-label="${esc(v.barLabel)}"`;
  if (b.segments) {
    const segs = Array.from({ length: b.segments.count }, (_, i) => `<span class="sg${i < b.segments!.filled ? ` on ${b.tone}` : ''}"></span>`).join('');
    return `<span class="bar segs" ${aria}>${segs}</span>`;
  }
  const tick = b.tick === null ? '' : `<span class="tick" data-l="${b.tick.toFixed(1)}"></span>`;
  return `<span class="bar" ${aria}><span class="fill ${b.tone}" data-w="${b.pct.toFixed(1)}"></span>${tick}</span>`;
}

/** One row. The whole row is one button (stretched over it); the bar stays readable as a progress bar. */
export function rowHtml(v: RowView): string {
  const check = v.tone === 'ok' ? CHECK : '';
  const hit =
    v.action === 'cat-drawer'
      ? `<button class="rowhit" data-a="cat-drawer" data-id="${esc(v.id)}" aria-label="${esc(`${v.name}: set an amount`)}"></button>`
      : `<button class="rowhit" data-a="category" data-cat="${esc(v.id)}" aria-label="${esc(`${v.name}: ${v.primary}`)}"></button>`;
  return `<div class="crow ${v.kind}${v.tone === 'muted' ? ' quiet' : ''}" data-row="${esc(v.id)}">${hit}
    <span class="ctop"><span class="cn">${esc(v.name)}</span><span class="cp${v.tone ? ` ${v.tone}` : ''}">${check}${esc(v.primary)}</span></span>
    ${barHtml(v)}
    <span class="cs">${esc(v.small)}</span></div>`;
}

// ---- Group headers ------------------------------------------------------------------------

/** A group's primary number for a mode (set-aside groups in moved and to move). */
export function groupPrimary(g: Pick<GroupDto, 'kind'>, cats: Category[], mode: RowMode): { text: string; over: boolean } {
  const tracked = cats.filter((k) => k.budgetCents > 0);
  const planned = tracked.reduce((s, k) => s + k.budgetCents, 0);
  const spent = (mode === 'spent' ? cats : tracked).reduce((s, k) => s + k.spentCents, 0);
  if (g.kind === 'set_aside') {
    if (mode === 'spent') return { text: `${money(spent)} moved`, over: false };
    if (mode === 'planned') return { text: money(planned), over: false };
    const left = tracked.reduce((s, k) => s + Math.max(0, k.budgetCents - k.spentCents), 0);
    return { text: left > 0 ? `${money(left)} to move` : 'All done', over: false };
  }
  return money3(mode, spent, planned);
}

/**
 * The name and the primary number on one line; the target (small, muted, the Budget tab's wording)
 * under the name, since name, target and number do not fit one line at phone width.
 */
export function groupHeader(d: OverviewDto, g: GroupDto, cats: Category[], mode: RowMode): string {
  const planned = cats.reduce((s, k) => s + k.budgetCents, 0);
  const share = groupShare(planned, d.totals.incomeCents, g.targetMinPct, g.targetMaxPct);
  const p = groupPrimary(g, cats, mode);
  const red = mode === 'remaining' && p.over;
  return `<h2 class="grouphd"><span class="gn">${esc(g.name)}</span><span class="gp${red ? ' bad' : ''}">${esc(p.text)}</span>${
    share.text ? `<small class="gt${share.warning ? ' warn' : ''}">${esc(share.text)}</small>` : ''
  }</h2>`;
}

